import { PeerService } from './PeerService';
import type { IceServer } from '../api/calls';

export interface MeshCallbacks {
  /** Emit an outbound signal to one user; the context ships it over WS. */
  onSignal: (toUserId: string, frame: Record<string, any>) => void;
  onParticipantAdded: (userId: string) => void;
  onParticipantStream: (userId: string, stream: MediaStream) => void;
  onParticipantStateChange: (userId: string, state: RTCPeerConnectionState) => void;
  onParticipantRemoved: (userId: string) => void;
}

/** Factory so tests can inject a fake PeerService. */
export type PeerFactory = (iceServers: IceServer[]) => PeerService;

/**
 * Grace period before dropping a peer that bounced to 'disconnected'. ICE
 * frequently flaps a healthy peer to 'disconnected' on a brief blip and then
 * recovers; only drop it if it has NOT recovered (or moved to 'failed') by the
 * time this elapses. Mirrors the 1:1 CallContext grace.
 */
const DISCONNECT_GRACE_MS = 6_000;

/**
 * Owns the N peer connections of one mesh group call: a single shared mic added
 * to every peer, the glare-free join protocol (smaller userId offers), and
 * per-peer ICE buffering. No React, no WebSocket — the wrapping context maps
 * onSignal() to WS sends and inbound frames to these methods.
 */
export class MeshCallSession {
  private peers = new Map<string, PeerService>();
  private pendingIce = new Map<string, RTCIceCandidateInit[]>();
  // Per-peer grace timers armed while a peer sits in 'disconnected'.
  private disconnectTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private localStream: MediaStream | null = null;
  private muted = false;

  constructor(
    private readonly selfId: string,
    private readonly iceServers: IceServer[],
    private readonly cb: MeshCallbacks,
    private readonly makePeer: PeerFactory = (ice) => new PeerService(ice),
  ) {}

  /** Capture the mic once (or accept an injected stream in tests). Shared. */
  async startLocalAudio(stream?: MediaStream): Promise<void> {
    if (this.localStream) return;
    if (stream) {
      this.localStream = stream;
      this.attachStreamToExistingPeers();
      return;
    }
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1, sampleRate: 48000 },
      });
    } catch {
      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    }
    this.attachStreamToExistingPeers();
  }

  /** Retroactively add the local stream to any peers created before getUserMedia resolved. */
  private attachStreamToExistingPeers(): void {
    if (!this.localStream) return;
    for (const peer of this.peers.values()) {
      peer.addLocalStream(this.localStream);
    }
  }

  get participantIds(): string[] { return Array.from(this.peers.keys()); }

  private ensurePeer(userId: string): PeerService {
    const existing = this.peers.get(userId);
    if (existing) return existing;
    const peer = this.makePeer(this.iceServers);
    peer.onIceCandidate = (candidate) => this.cb.onSignal(userId, { type: 'gcall-ice', candidate });
    peer.onRemoteStream = (s) => this.cb.onParticipantStream(userId, s);
    peer.onConnectionStateChange = (st) => {
      this.cb.onParticipantStateChange(userId, st);
      if (st === 'connected') {
        // Recovered (or first connect): cancel any pending disconnect grace.
        this.clearDisconnectTimer(userId);
      } else if (st === 'failed' || st === 'closed') {
        // A non-recovering drop — terminal, remove the peer at once.
        this.clearDisconnectTimer(userId);
        this.removePeer(userId);
      } else if (st === 'disconnected') {
        // 'disconnected' is frequently transient (ICE can recover); dropping it
        // immediately would tear a small group call down on a brief network
        // blip. Arm a short grace timer instead: a return to 'connected' cancels
        // it, a move to 'failed' removes the peer above, and only a peer still
        // gone when this fires is dropped — which lets the last-peer-left
        // auto-end kick in even if the connection never reaches 'failed'.
        if (!this.disconnectTimers.has(userId)) {
          this.disconnectTimers.set(userId, setTimeout(() => {
            this.disconnectTimers.delete(userId);
            this.removePeer(userId);
          }, DISCONNECT_GRACE_MS));
        }
      }
    };
    if (this.localStream) peer.addLocalStream(this.localStream);
    this.peers.set(userId, peer);
    this.cb.onParticipantAdded(userId);
    return peer;
  }

  /** Newcomer announced a join: smaller id offers, larger id announces presence. */
  async onJoin(userId: string): Promise<void> {
    if (userId === this.selfId || this.peers.has(userId)) return;
    if (this.selfId < userId) {
      const peer = this.ensurePeer(userId);
      const offer = await peer.createOffer();
      this.cb.onSignal(userId, { type: 'gcall-offer', sdp: offer });
    } else {
      this.cb.onSignal(userId, { type: 'gcall-present', to_user_id: userId });
    }
  }

  /** An existing participant announced presence: if we're the smaller id, offer. */
  async onPresence(userId: string): Promise<void> {
    if (userId === this.selfId || this.peers.has(userId)) return;
    if (this.selfId < userId) {
      const peer = this.ensurePeer(userId);
      const offer = await peer.createOffer();
      this.cb.onSignal(userId, { type: 'gcall-offer', sdp: offer });
    }
  }

  async handleOffer(userId: string, sdp: RTCSessionDescriptionInit): Promise<void> {
    const peer = this.ensurePeer(userId);
    await peer.setRemoteDescription(sdp);
    await this.flushIce(userId);
    const answer = await peer.createAnswer();
    this.cb.onSignal(userId, { type: 'gcall-answer', sdp: answer });
  }

  async handleAnswer(userId: string, sdp: RTCSessionDescriptionInit): Promise<void> {
    const peer = this.peers.get(userId);
    if (!peer) return;
    await peer.setRemoteDescription(sdp);
    await this.flushIce(userId);
  }

  async handleIce(userId: string, candidate: RTCIceCandidateInit): Promise<void> {
    const peer = this.peers.get(userId);
    if (peer) { await peer.addIceCandidate(candidate); return; }
    const q = this.pendingIce.get(userId) ?? [];
    q.push(candidate);
    this.pendingIce.set(userId, q);
  }

  private async flushIce(userId: string): Promise<void> {
    const peer = this.peers.get(userId);
    const q = this.pendingIce.get(userId);
    if (!peer || !q) return;
    this.pendingIce.delete(userId);
    for (const c of q) await peer.addIceCandidate(c);
  }

  removePeer(userId: string): void {
    const peer = this.peers.get(userId);
    if (!peer) return;
    this.clearDisconnectTimer(userId);
    this.peers.delete(userId);
    this.pendingIce.delete(userId);
    peer.close();
    this.cb.onParticipantRemoved(userId);
  }

  /** Cancel a peer's pending 'disconnected' grace timer, if any. */
  private clearDisconnectTimer(userId: string): void {
    const t = this.disconnectTimers.get(userId);
    if (t) { clearTimeout(t); this.disconnectTimers.delete(userId); }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.localStream?.getAudioTracks().forEach((t) => { t.enabled = !muted; });
  }

  get isMuted(): boolean { return this.muted; }

  closeAll(): void {
    // Cancel every pending disconnect grace timer so none fires after teardown.
    for (const t of this.disconnectTimers.values()) clearTimeout(t);
    this.disconnectTimers.clear();
    for (const [, peer] of this.peers) peer.close();
    this.peers.clear();
    this.pendingIce.clear();
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.localStream = null;
  }
}
